import {BaseModel} from '../base/BaseModel';
import {UserModel} from '../users/UserModel';
import {RegexUtils} from '../../utils/RegexUtils';
import {Weather2Collection} from '../../collection/weather/Weather2Collection';
import { PermissionCollection } from '../../collection/users/PermissionCollection';
import { BaseFilterModel } from '../filter/BaseFilterModel';

/**
 * @class FarmModel
 * @extends BaseModel
 *
 * @property {object} attributes
 * @property {string<UUID>} attributes.uuid
 * @property {string<UUID>} attributes.creatorId
 * @property {Array<UUID>} attributes.fieldUuids
 * @property {string} attributes.name
 * @property {string} attributes.etag
 */

export class FarmModel extends BaseModel {

    initialize() {

        this.microservice = 'farms';
        this.resource = 'v2/farms';
    }

    get defaults() {

        return {
            name: undefined,
            creatorId: undefined,
            latitude: undefined,
            longitude: undefined,
            etag: undefined,
        };
    }

    validateProperty(property, attributes) {

        if (property === 'name') {

            attributes.name = _.trim(attributes.name);
            return FarmModel.validateName(attributes.name);
        }
    }

    /**
     * @description There is a race condition after creating a farm
     *              wait until user is authorized to access his farm
     *              try to fetch the farm 10 times
     *
     * @returns {object} $.Deferred()
     */
    ensureAccess() {

        let retry = 10;
        let interval = 200;
        let promise = $.Deferred();
        let fetched = this.pollFarmAccessLevel();

        this._retryIntervall = setInterval(() => {

            if (retry <= 1) {

                clearInterval(this._retryIntervall);
                console.error('Failed to fetch farm after 10 retries');
                promise.reject();
            }
            else if (fetched.state() === 'rejected') {

                fetched = this.pollFarmAccessLevel();
                retry--;
            }

        }, interval);

        fetched.done((permissionCollection) => {

            clearInterval(this._retryIntervall);
            promise.resolve(permissionCollection);
        });

        return promise;
    }

    /**
     * @description Fetching the farm details using Jquery ajax
     *              in order to include extra header to ignore 403
     *
     * @returns {jQuery.Deferred}
     */
    pollFarmAccessLevel() {

        const userUuid = new UserModel().attributes.uuid;

        const permissions = new PermissionCollection();

        return permissions.setFilterParams(new BaseFilterModel({
                objectUuid : this.attributes.uuid,
                objectType: 'FARM',
                granteeUuid: userUuid,
            }))
            .done((permissionCollection) => {
                if(permissionCollection && permissionCollection.models[0]) {

                    const access = permissionCollection.models[0].attributes.access;

                    this.set({access});
                }
            });
    }

    /**
     * @description This method check access and return true if it has SHARE Access
     *
     * @returns {Boolean} true|false
     */
    hasSharedAccess() {

        return this.get('access') === UserModel.ACCESS_LEVEL.SHARE;
    }

    /**
     * @description This method check access and return true if it has WRITE Access
     *
     * @returns {Boolean} true|false
     */
    hasWriteAccess() {

        return this.get('access') === UserModel.ACCESS_LEVEL.WRITE;
    }

    hasWriteOrShareAccess () {

        return ((this.get('access') === UserModel.ACCESS_LEVEL.WRITE) ||
            (this.get('access') === UserModel.ACCESS_LEVEL.SHARE));
    }

    hasContractorLimitedAccess () {
        return this.get('access') === UserModel.ACCESS_LEVEL.CONTRACTOR_LIMITED_FARM_ACCESS;
    }

    hasWriteOrShareOrLimitedAccess () {
        return ((this.get('access') === UserModel.ACCESS_LEVEL.WRITE) ||
            (this.get('access') === UserModel.ACCESS_LEVEL.SHARE) ||
            (this.get('access') === UserModel.ACCESS_LEVEL.CONTRACTOR_LIMITED_FARM_ACCESS));
    }

    /**
     * @description updates timezone in BaseModel
     */
    updateBaseTimezone() {

        let timezone = this.get('timezone');

        if (timezone) {

            BaseModel.timezone = timezone;
            Radio.channel('APP').trigger('TZ_CHANGED', timezone);
        }
    }

    /**
     * @description Trims the name of the farm
     */
    trimName() {

        let trimmedName = _.trim(this.get('name'));

        this.set('name', trimmedName);
    }

    /**
     * @description validates name of a farm
     *              should not contain any special characters
     *              length should be between 2 and 100 characters
     *
     * @returns {object} Error message and property name
     */
    static validateName(name) {

        if (!name) {

            return {
                property: 'name',
                message: __('FARM_NAME_EMPTY')
            };
        }

        if (!RegexUtils.getFarmNameRegex().test(name)) {

            return {
                property: 'name',
                message: __('NAME_INVALID_CHAR')
            };
        }

        let min = 2, max = 100;

        if (name.length < min) {

            return {
                property: 'name',
                message: __('NAME_INVALID_LENGTH_MIN')
            };
        }

        if (name.length > max) {

            return {
                property: 'name',
                message: __('NAME_INVALID_LENGTH_MAX')
            };
        }
    }

    /**
     * @description checks if farm has reached maximum field number defined in app config
     * 
     * @param {number} offset - for calculation with field import
     * 
     * @returns {object} results of check with maxFieldNumber, overflow and isMaxFieldNumberReached
     */
    checkMaxFieldNumber(offset) {

        const CURRENT_USER = Radio.channel('fm').request('get:sessObj', 'CURRENT_USER');
        const CURRENT_FIELDS = Radio.channel('fm').request('get:sessObj', 'CURRENT_FIELDS');

        const appConfigSelector = 'MAXIMUM_FIELD_NUMBER';

        const appConfig = CURRENT_USER.get('applicationSettings').getCountryConfig([appConfigSelector], []);
        let fieldCount = CURRENT_FIELDS.length;

        if (offset) {
            fieldCount += offset;
        }

        return {
            isMaxFieldNumberReached: appConfig[appConfigSelector] <= fieldCount,
            overflow: fieldCount - appConfig[appConfigSelector],
            maxFieldNumber: appConfig[appConfigSelector]
        };
    }

    fetchDailyAndHourlyWeatherData({ days = 10 }) {

        let deferred = $.Deferred();

        const farmUuid = this.get('uuid');

        if (!this.get('weather')) {
            const weather = new Weather2Collection();

            weather.fetchDailyWeatherPrediction({ foreignUuid: farmUuid, days })
                .always(() => {

                    this.set('weather', weather);
                    deferred.resolve();
                });
        } else {
            return deferred.resolve();
        }

        return deferred;
    }

    serializeAccess () {

        let access;

        if (!this.get('access')) {
            access = '';
        } else if (this.hasSharedAccess()) {
            access = __('FARM_ROLE.ADMINISTRATOR');
        } else if (this.hasWriteAccess()) {
            access = __('FARM_ROLE.CONTRIBUTOR');
        } else if (this.hasContractorLimitedAccess()) {
            access = __('FARM_ROLE.CONTRACTOR_LIMITED_FARM_ACCESS');
        } else {
            access = __('FARM_ROLE.VIEWER');
        }

        return access;
    }

    static parseGQL(data) {

        const {
            uuid,
            name,
            creatorUuid,
            access,
            timezone,
            latitude,
            longitude,
        } = data;

        return {
            uuid,
            name,
            creatorId: creatorUuid,
            access,
            timezone,
            latitude,
            longitude,
        };
    }
}



// WEBPACK FOOTER //
// ./shared/model/farms/FarmModel.js